import { defineStore } from 'pinia'
import { ref, watchEffect } from 'vue'
import { db } from '@/firebaseConfig'
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore'
import { auth } from '@/firebaseConfig'
import { onAuthStateChanged } from 'firebase/auth'


export const useSavedAddressStore = defineStore('savedAddress', () => {
  const savedAddress = ref(null)
  const isLoading = ref(false)
  const error = ref(null)
  const isAuthReady = ref(false)
  let unsubscribe = null

  // Private function to fetch address
  async function fetchSavedAddress() {
    if (!auth.currentUser) return

    isLoading.value = true
    error.value = null

    try {
      // console.log('Fetching address for user:', auth.currentUser?.uid)
      const addressesRef = collection(db, 'addresses')
      const q = query(
        addressesRef,
        where('userId', '==', auth.currentUser?.uid),
      )

      const querySnapshot = await getDocs(q)

      if (!querySnapshot.empty) {
        const addressDoc = querySnapshot.docs[0]
        savedAddress.value = { id: addressDoc.id, ...addressDoc.data() }
      } else {
        console.log('No address found')
        savedAddress.value = null
      }
    } catch (err) {
      console.error('Error fetching address:', err)
      error.value = err.message
      savedAddress.value = null
    } finally {
      isLoading.value = false
    }
  }

  unsubscribe = onAuthStateChanged(auth, async user => {
    isAuthReady.value = true
    if (user) {
      await fetchSavedAddress()
    } else {
      savedAddress.value = null
      error.value = null
    }
  })

  // Set up auth state observer
  watchEffect(onCleanup => {
    unsubscribe = onAuthStateChanged(auth, user => {
      if (user) {
        fetchSavedAddress()
      } else {
        savedAddress.value = null
        error.value = null
      }
    })

    // Cleanup function
    onCleanup(() => {
      if (unsubscribe) {
        unsubscribe()
      }
    })
  })



  async function saveCustomerContactDetails(customerData) {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to save contact details')
    }

    isLoading.value = true
    error.value = null

    try {
      // Create contact details object with only specified fields
      const contactDetails = {
        userId: auth.currentUser?.uid,
        contactName: customerData.contactName,
        contactPhoneNumber: customerData.contactPhoneNumber,
        contactEmail: customerData.contactEmail,
        residentialAddress: customerData.residentialAddress,
        btwId: customerData.btwId,
        kvk: customerData.kvk,
        wantCompanyInvoice: customerData.wantCompanyInvoice,
      }

      // Add company details if it's a company
      // if (customerData.isCompany) {
      //   contactDetails.companyName = customerData.companyName
      //   contactDetails.companyNIP = customerData.companyNIP
      //   contactDetails.companyAddress = customerData.companyAddress
      //   contactDetails.companyCity = customerData.companyCity
      //   contactDetails.companyPostCode = customerData.companyPostCode
      // }

      if (!validateAddressData(contactDetails)) {
        throw new Error('Invalid contact details data')
      }

      let docId

      if (savedAddress.value) {
        // Update existing address
        const docRef = doc(db, 'addresses', savedAddress.value.id)
        contactDetails.updatedAt = serverTimestamp()
        await updateDoc(docRef, contactDetails)
        docId = savedAddress.value.id
      } else {
        // Add new address
        contactDetails.createdAt = serverTimestamp()
        const docRef = await addDoc(collection(db, 'addresses'), contactDetails)
        docId = docRef.id
      }

      // Update store state with new data
      savedAddress.value = { id: docId, ...contactDetails }
      return docId
    } catch (err) {
      console.error('Error saving contact details:', err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  function validateAddressData(address) {
    if (!address.userId) {
      return false
    }
    return true
  }

  function resetState() {
    savedAddress.value = null
    isLoading.value = false
    error.value = null
  }

  return {
    savedAddress,
    isLoading,
    error,
    isAuthReady, // expose this so components can check if auth is ready
    saveCustomerContactDetails,
    resetState,
  }
})
